import { useTranslation } from "react-i18next";
import { MarketingNav } from "@/components/landing/MarketingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { SEOHead } from "@/components/SEOHead";
import { SchemaMarkup, generateBreadcrumbSchema } from "@/components/SchemaMarkup";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export default function TermsOfServicePage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith("pt");

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Termos de Serviço | RevOps AI" : "Terms of Service | RevOps AI"}
        description={isPt ? "Termos de serviço do RevOps AI. Leia nossos termos e condições de uso da plataforma." : "RevOps AI terms of service. Read our terms and conditions of use."}
        path="/terms"
        
      />
      <SchemaMarkup schema={[generateBreadcrumbSchema([
        { name: 'Home', url: 'https://revops.digitais.ai/' },
        { name: isPt ? 'Termos' : 'Terms', url: 'https://revops.digitais.ai/terms' },
      ])]} />
      <MarketingNav />
      <main className="py-16 md:py-24">
        <div className="container max-w-3xl mx-auto">
          <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Termos de Serviço' : 'Terms of Service' }]} />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1A1F3C] mb-2">
            {isPt ? "Termos de Serviço" : "Terms of Service"}
          </h1>
          <p className="text-sm text-[#1A1F3C]/40 mb-10">
            {isPt ? "Última atualização: 25 de março de 2026" : "Last updated: March 25, 2026"}
          </p>

          <div className="prose prose-lg max-w-none text-[#1A1F3C]/70 [&>h2]:text-[#1A1F3C] [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-10 [&>h2]:mb-4">
            {isPt ? (
              <>
                <h2>1. Aceitação dos Termos</h2>
                <p>
                  Ao acessar ou usar a plataforma RevOps AI, você concorda com estes Termos de Serviço. Se você está
                  usando o serviço em nome de uma organização, você declara ter autoridade para vincular a organização a
                  estes termos.
                </p>

                <h2>2. Descrição do Serviço</h2>
                <p>
                  RevOps AI é uma plataforma SaaS de Revenue Intelligence que integra com CRMs para fornecer análises de
                  receita, forecasts matemáticos, diagnósticos de funil e recomendações acionáveis para times de vendas
                  e operações de receita.
                </p>

                <h2>3. Conta e Acesso</h2>
                <p>
                  Você é responsável por manter a confidencialidade das credenciais de acesso à sua conta. Cada conta é
                  pessoal e intransferível. O compartilhamento de credenciais viola estes termos.
                </p>
                <p>
                  O acesso é gerenciado por organização, com controle de permissões baseado em perfis (admin, gerente,
                  membro, visualizador).
                </p>

                <h2>4. Planos e Pagamento</h2>
                <p>
                  Os planos disponíveis e preços são exibidos na página de preços. Os pagamentos são processados pelo
                  Stripe. Planos mensais podem ser cancelados a qualquer momento. Planos anuais são cobrados
                  antecipadamente.
                </p>
                <p>
                  Oferecemos um período de teste gratuito de 7 dias para novos clientes. Após o término, é necessário
                  escolher um plano pago para continuar acessando a plataforma.
                </p>

                <h2>5. Uso Aceitável</h2>
                <p>Você concorda em:</p>
                <ul>
                  <li>Usar a plataforma apenas para fins legítimos de negócio</li>
                  <li>Não tentar acessar dados de outras organizações</li>
                  <li>Não realizar engenharia reversa, descompilar ou desmontar o software</li>
                  <li>Não usar a plataforma para armazenar ou transmitir conteúdo malicioso</li>
                  <li>Não exceder os limites de uso do seu plano</li>
                </ul>

                <h2>6. Dados e Propriedade</h2>
                <p>
                  Seus dados de CRM permanecem de sua propriedade. A RevOps AI atua como processadora de dados,
                  utilizando-os exclusivamente para fornecer o serviço contratado.
                </p>
                <p>
                  Podemos utilizar dados agregados e anonimizados para melhorar nossos algoritmos e modelos de IA, sem
                  identificar sua organização ou indivíduos.
                </p>

                <h2>7. Propriedade Intelectual</h2>
                <p>
                  A plataforma RevOps AI, incluindo software, design, algoritmos, modelos de IA e marca, é propriedade
                  exclusiva da RevOps AI. Nenhum direito de propriedade intelectual é transferido ao cliente.
                </p>

                <h2>8. Disponibilidade e SLA</h2>
                <p>
                  Nos esforçamos para manter disponibilidade de 99.9%. Manutenções programadas serão comunicadas com
                  antecedência. Não somos responsáveis por indisponibilidades causadas por seus provedores de CRM ou
                  internet.
                </p>

                <h2>9. Limitação de Responsabilidade</h2>
                <p>
                  A RevOps AI fornece análises e recomendações baseadas nos dados disponíveis. Não garantimos resultados
                  específicos de receita. As recomendações da plataforma são de natureza consultiva e a decisão final é
                  sempre do cliente.
                </p>
                <p>Nossa responsabilidade total está limitada ao valor pago pelo serviço nos últimos 12 meses.</p>

                <h2>10. Cancelamento</h2>
                <p>
                  Você pode cancelar sua assinatura a qualquer momento. Após o cancelamento, o acesso continua até o
                  final do período já pago. Dados são retidos por 30 dias após o cancelamento, conforme nossa Política
                  de Privacidade.
                </p>

                <h2>11. Alterações nos Termos</h2>
                <p>
                  Podemos atualizar estes termos periodicamente. Mudanças significativas serão notificadas com pelo
                  menos 30 dias de antecedência. O uso continuado após a notificação constitui aceitação dos novos
                  termos.
                </p>

                <h2>12. Lei Aplicável</h2>
                <p>
                  Estes termos são regidos pelas leis da República Federativa do Brasil. Quaisquer disputas serão
                  submetidas ao foro da comarca de Joinville, SC.
                </p>

                <h2>13. Contato</h2>
                <p>
                  Para dúvidas sobre estes termos:
                  <br />
                  <strong>Email:</strong> legal@digitais.ai
                </p>
              </>
            ) : (
              <>
                <h2>1. Acceptance of Terms</h2>
                <p>
                  By accessing or using the RevOps AI platform, you agree to these Terms of Service. If you are using
                  the service on behalf of an organization, you represent that you have the authority to bind the
                  organization to these terms.
                </p>

                <h2>2. Service Description</h2>
                <p>
                  RevOps AI is a SaaS Revenue Intelligence platform that integrates with CRMs to provide revenue
                  analysis, mathematical forecasts, funnel diagnostics, and actionable recommendations for sales and
                  revenue operations teams.
                </p>

                <h2>3. Account and Access</h2>
                <p>
                  You are responsible for maintaining the confidentiality of your account credentials. Each account is
                  personal and non-transferable. Sharing credentials violates these terms.
                </p>
                <p>
                  Access is managed per organization, with permission control based on profiles (admin, manager, member,
                  viewer).
                </p>

                <h2>4. Plans and Payment</h2>
                <p>
                  Available plans and pricing are displayed on the pricing page. Payments are processed by Stripe.
                  Monthly plans can be canceled at any time. Annual plans are billed in advance.
                </p>
                <p>
                  We offer a 7-day free trial for new customers. After the trial ends, a paid plan is required to
                  continue accessing the platform.
                </p>

                <h2>5. Acceptable Use</h2>
                <p>You agree to:</p>
                <ul>
                  <li>Use the platform only for legitimate business purposes</li>
                  <li>Not attempt to access other organizations' data</li>
                  <li>Not reverse engineer, decompile, or disassemble the software</li>
                  <li>Not use the platform to store or transmit malicious content</li>
                  <li>Not exceed the usage limits of your plan</li>
                </ul>

                <h2>6. Data and Ownership</h2>
                <p>
                  Your CRM data remains your property. RevOps AI acts as a data processor, using it exclusively to
                  provide the contracted service.
                </p>
                <p>
                  We may use aggregated and anonymized data to improve our algorithms and AI models, without identifying
                  your organization or individuals.
                </p>

                <h2>7. Intellectual Property</h2>
                <p>
                  The RevOps AI platform, including software, design, algorithms, AI models, and brand, is the exclusive
                  property of RevOps AI. No intellectual property rights are transferred to the client.
                </p>

                <h2>8. Availability and SLA</h2>
                <p>
                  We strive to maintain 99.9% availability. Scheduled maintenance will be communicated in advance. We
                  are not responsible for unavailability caused by your CRM providers or internet.
                </p>

                <h2>9. Limitation of Liability</h2>
                <p>
                  RevOps AI provides analyses and recommendations based on available data. We do not guarantee specific
                  revenue results. Platform recommendations are advisory in nature and the final decision is always the
                  client's.
                </p>
                <p>Our total liability is limited to the amount paid for the service in the last 12 months.</p>

                <h2>10. Cancellation</h2>
                <p>
                  You can cancel your subscription at any time. After cancellation, access continues until the end of
                  the already-paid period. Data is retained for 30 days after cancellation, per our Privacy Policy.
                </p>

                <h2>11. Changes to Terms</h2>
                <p>
                  We may update these terms periodically. Significant changes will be notified at least 30 days in
                  advance. Continued use after notification constitutes acceptance of the new terms.
                </p>

                <h2>12. Governing Law</h2>
                <p>
                  These terms are governed by the laws of the Federative Republic of Brazil. Any disputes will be
                  submitted to the jurisdiction of Joinville, SC, Brazil.
                </p>

                <h2>13. Contact</h2>
                <p>
                  For questions about these terms:
                  <br />
                  <strong>Email:</strong> legal@digitais.ai
                </p>
              </>
            )}
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}
